import React, { useState, useRef } from 'react';
import styles from './SupportForm.module.scss';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { httpPostWithCsrfToken } from '../../utils/Http';
import FormInputField from '../FormInputField/FormInputField';
import FormSelectField from '../FormSelectField/FormSelectField';
import FormTextAreaField from '../FormTextAreaField/FormTextAreaField';
import FormPolicyAcceptanceField from '../FormPolicyAcceptanceField/FormPolicyAcceptanceField';
import FormCaptchaField from '../FormCaptchaField/FormCaptchaField';
import Button from '../Button';

const SupportForm = ({
    countries,
    products,
    problems,
    policy,
    sitekey,
    submitUrl,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const captchaRef = useRef(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [showCaptchaError, setShowCaptchaError] = useState(null);
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const onSubmit = (values) => {
        if (!captchaToken) {
            setShowCaptchaError(true);
            return;
        }

        const selectedCountryLabel = countries.find(
            (country) => country.value === values.country
        ).label;

        setIsLoading(true);
        setShowErrorMessage(false);
        setShowCaptchaError(false);

        const data = {
            form: {
                ...values,
                countrylabel: selectedCountryLabel,
            },
            captchaToken: captchaToken,
        };

        httpPostWithCsrfToken(submitUrl, data)
            .then(async (res) => {
                if (res && res.success) {
                    setShowSuccess(true);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                }
            })
            .catch(() => {
                setIsLoading(false);
                setShowErrorMessage(true);
                setCaptchaToken(null);
                captchaRef.current.reset();
            });
    };

    if (showSuccess) {
        return (
            <div className={styles['SupportForm']}>
                <h2 className={styles['SupportForm__Success']}>Thank you!</h2>
            </div>
        );
    }

    return (
        <div
            className={classNames(styles['SupportForm'], {
                [styles['SupportForm--Loading']]: isLoading,
            })}>
            <form
                className={styles['SupportForm__Form']}
                onSubmit={handleSubmit(onSubmit)}>
                <div
                    className={classNames(
                        styles['SupportForm__Row'],
                        styles['SupportForm__Row--Half']
                    )}>
                    <div className={styles['SupportForm__Field']}>
                        <FormInputField
                            type="text"
                            name="name"
                            label="Name *"
                            placeholder="Name"
                            errors={errors}
                            register={{
                                ...register('name', { required: true }),
                            }}
                        />
                    </div>
                    <div className={styles['SupportForm__Field']}>
                        <FormInputField
                            type="email"
                            name="email"
                            label="E-mail address *"
                            placeholder="E-mail address"
                            errors={errors}
                            register={{
                                ...register('email', { required: true }),
                            }}
                        />
                    </div>
                </div>

                <div
                    className={classNames(
                        styles['SupportForm__Row'],
                        styles['SupportForm__Row--Half']
                    )}>
                    <div className={styles['SupportForm__Field']}>
                        <FormInputField
                            type="text"
                            name="city"
                            label="City"
                            placeholder="City"
                            errors={errors}
                            helptext="If your unit needs service, this allows us to recommend the closest service center"
                            register={{
                                ...register('city', { required: false }),
                            }}
                        />
                    </div>
                    <div className={styles['SupportForm__Field']}>
                        <FormSelectField
                            name="country"
                            label="Country *"
                            errors={errors}
                            options={countries}
                            register={{
                                ...register('country', { required: true }),
                            }}
                            modifiers={['White']}
                        />
                    </div>
                </div>

                <div
                    className={classNames(
                        styles['SupportForm__Row'],
                        styles['SupportForm__Row--Half']
                    )}>
                    <div className={styles['SupportForm__Field']}>
                    <FormInputField
                            type="text"
                            name="pointofpurchase"
                            label="Point of purchase"
                            placeholder="Point of purchase"
                            errors={errors}
                            helptext="The music store/retailer where you bought the instrument"
                            register={{
                                ...register('pointofpurchase', {
                                    required: false,
                                }),
                            }}
                        />
                    </div>
                    <div className={styles['SupportForm__Field']}>
                        <FormSelectField
                            name="product"
                            label="Instrument model *"
                            errors={errors}
                            options={products}
                            register={{
                                ...register('product', { required: true }),
                            }}
                            modifiers={['White']}
                        />
                    </div>
                </div>

                <div
                    className={classNames(
                        styles['SupportForm__Row'],
                        styles['SupportForm__Row--Half']
                    )}>
                    <div className={styles['SupportForm__Field']}>
                        <FormInputField
                            type="text"
                            name="instrumentos"
                            label="Instrument OS version"
                            placeholder="Instrument OS version"
                            errors={errors}
                            helptext="Displayed at power up"
                            register={{
                                ...register('instrumentos', {
                                    required: false,
                                }),
                            }}
                        />
                    </div>
                    <div className={styles['SupportForm__Field']}>
                        <FormInputField
                            type="text"
                            name="computeros"
                            label="Computer OS version"
                            placeholder="Computer OS version"
                            errors={errors}
                            helptext="Required for software related problems"
                            register={{
                                ...register('computeros', { required: false }),
                            }}
                        />
                    </div>
                </div>

                <div
                    className={classNames(
                        styles['SupportForm__Row'],
                        styles['SupportForm__Row--Half']
                    )}>
                    <div className={styles['SupportForm__Field']}>
                        <FormInputField
                            type="text"
                            name="serial"
                            label="Serial No *"
                            placeholder="Serial No"
                            errors={errors}
                            helptext="Printed on the rear panel"
                            register={{
                                ...register('serial', { required: true }),
                            }}
                        />
                    </div>
                    <div className={styles['SupportForm__Field']}>
                        <FormSelectField
                            name="problem"
                            label="Type of problem *"
                            errors={errors}
                            options={problems}
                            register={{
                                ...register('problem', { required: true }),
                            }}
                            modifiers={['White']}
                        />
                    </div>
                </div>

                <div className={styles['SupportForm__Row']}>
                    <div className={styles['SupportForm__Field']}>
                        <FormTextAreaField
                            name="description"
                            label="Detailed description of problem/symptom *"
                            errors={errors}
                            register={{
                                ...register('description', { required: true }),
                            }}
                        />
                    </div>
                </div>
                <div className={styles['SupportForm__Row']}>
                    <div className={styles['SupportForm__Field']}>
                        <FormPolicyAcceptanceField
                            name="policy"
                            label={policy}
                            errors={errors}
                            register={{
                                ...register('policy', { required: true }),
                            }}
                        />
                    </div>
                </div>

                <div className={styles['FeedbackForm__Row']}>
                    <div className={styles['FeedbackForm__Field']}>
                        <FormCaptchaField
                            sitekey={sitekey}
                            captchaRef={captchaRef}
                            setCaptchaToken={setCaptchaToken}
                            showErrorMessage={showCaptchaError && !captchaToken}
                        />
                    </div>
                </div>

                <div className={styles['SupportForm__Row']}>
                    <div className={styles['SupportForm__Button']}>
                        <Button buttonType="submit" text={'Submit'} />
                    </div>
                    {showErrorMessage && (
                        <p className={styles['SupportForm__Error']}>
                            An error occurred while sending the email. Please
                            try again.
                        </p>
                    )}
                </div>
            </form>
        </div>
    );
};

SupportForm.propTypes = {
    countries: PropTypes.array,
    products: PropTypes.array,
    problems: PropTypes.array,
    sitekey: PropTypes.string,
};

export default SupportForm;
